import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { IItem } from 'models/item';
import useItem from 'services/useItem.service';
import type { Interaction } from 'services/useItem.service';
import useItems from 'services/useItems.service';
import useSet from 'services/useSet.service';
import useAnnotations from 'services/useAnnotations';

type Props = {
  setId: string;
  courseId: string;
  initialItemId?: string;
  children: ReactNode;
};

type ContextType = {
  setId: string;
  items: any;
  item: Interaction;
  rawItem: IItem | null;
  reorder: (newOrder: string[]) => void;
  create: (templateType: string, answerType?: string, instructionalContentSubtype?: string) => any;
  isLoadingNewItem: boolean;
  set: any;
  renameSet: (name: string) => void;
  updateSetImage: (imageId: string) => void;
  courseId: string;
};

export const AuthoringContext = createContext<ContextType | undefined>(undefined);

export function AuthoringContextProvider({ setId, courseId, initialItemId, children }: Props) {
  const { set, renameSet, updateSetImage } = useSet(setId);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const { items, reorder, create, service: itemsService, isLoadingNewItem } = useItems(setId, initialItemId);
  const { item, addEmptyFacetOnItemCreationIfNeeded } = useItem(setId, selectedItem, itemsService);
  const annotations = useAnnotations(setId, selectedItem?.id);

  useEffect(() => {
    setSelectedItem(items.find((item) => item.selected));
  }, [items]);

  // some pages require extra setup on front end when creation
  // addEmptyFacetOnItemCreationIfNeeded checks if it's one of those pages and does the extra setup
  const createNewItem = (templateType, answerType, instructionalContentSubtype) => {
    create(templateType, answerType, instructionalContentSubtype, async (templateType, newItemId) => {
      let emptyAnnotation = null;
      if (instructionalContentSubtype === 'instructional_document') {
        emptyAnnotation = await annotations.create({ pathParams: { id: newItemId } });
      }
      const emptyFacet = await addEmptyFacetOnItemCreationIfNeeded(templateType, newItemId);
      return { emptyFacet, emptyAnnotation };
    });
  };

  const contextValues: ContextType = {
    setId,
    items,
    reorder,
    create: createNewItem,
    item,
    rawItem: selectedItem,
    isLoadingNewItem,
    set,
    renameSet,
    updateSetImage,
    courseId,
  };

  return <AuthoringContext.Provider value={contextValues}>{children}</AuthoringContext.Provider>;
}

export function useAuthoringContext() {
  const context = useContext(AuthoringContext);
  if (context === undefined) {
    throw new Error('useAuthoringContext must be used within a AuthoringContextProvider');
  }
  return context;
}
